import { useHandler } from "@redotech/react-util/hook";
import {
  FedexClaimInfo,
  FedexDamageInfo,
  FedexDamageToContents,
  FedexDamageToOuter,
  FedexPackagingMaterial,
} from "@redotech/redo-package-protection-common/shipping-claims/carrier-claim-info/fedex";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";

function enumOptions<T extends string>(values: T[]): RedoListItem<T>[] {
  return values.map((v) => ({ type: "text", text: v, value: v, id: v }));
}

const DAMAGE_TO_CONTENTS_OPTIONS = enumOptions(
  Object.values(FedexDamageToContents),
);
const DAMAGE_TO_OUTER_OPTIONS = enumOptions(Object.values(FedexDamageToOuter));
const PACKAGING_MATERIAL_OPTIONS = enumOptions(
  Object.values(FedexPackagingMaterial),
);

export type PartialFedexDamageInfo = Partial<FedexDamageInfo>;

export function isFedexDamageInfoComplete(
  d: PartialFedexDamageInfo,
): d is FedexDamageInfo {
  if (!d.damageToContents || !d.damageToOuter || !d.packagingMaterials) {
    return false;
  }
  if (d.damageToOuter === FedexDamageToOuter.OTHER && !d.damageToOuterOther) {
    return false;
  }
  if (
    d.packagingMaterials === FedexPackagingMaterial.OTHER &&
    !d.packagingMaterialsOther
  ) {
    return false;
  }
  return true;
}

export function buildFedexClaimInfo(
  d: PartialFedexDamageInfo,
): FedexClaimInfo | null {
  if (!isFedexDamageInfoComplete(d)) {
    return null;
  }
  return { damage: d };
}

interface FedexClaimInfoSectionProps {
  value: PartialFedexDamageInfo;
  setValue: (next: PartialFedexDamageInfo) => void;
}

export const FedexClaimInfoSection = memo(function FedexClaimInfoSection({
  value,
  setValue,
}: FedexClaimInfoSectionProps) {
  const update = useHandler((patch: Partial<FedexDamageInfo>) => {
    setValue({ ...value, ...patch });
  });

  return (
    <Flex dir="column" gap="md">
      <Text fontSize="sm" fontWeight="semibold">
        FedEx damage details
      </Text>

      <RedoSingleSelectDropdownInput
        label="Type of damage to contents"
        options={DAMAGE_TO_CONTENTS_OPTIONS}
        optionSelected={(item) => update({ damageToContents: item.item.value })}
        selectedItem={DAMAGE_TO_CONTENTS_OPTIONS.find(
          (o) => o.value === value.damageToContents,
        )}
      />

      <RedoSingleSelectDropdownInput
        label="Type of damage to outer packaging"
        options={DAMAGE_TO_OUTER_OPTIONS}
        optionSelected={(item) => update({ damageToOuter: item.item.value })}
        selectedItem={DAMAGE_TO_OUTER_OPTIONS.find(
          (o) => o.value === value.damageToOuter,
        )}
      />

      {value.damageToOuter === FedexDamageToOuter.OTHER && (
        <RedoTextInput
          label="Describe the damage to outer packaging"
          maxLength={66}
          setValue={(s) => update({ damageToOuterOther: s })}
          value={value.damageToOuterOther ?? ""}
        />
      )}

      <RedoSingleSelectDropdownInput
        label="Packaging materials"
        options={PACKAGING_MATERIAL_OPTIONS}
        optionSelected={(item) =>
          update({ packagingMaterials: item.item.value })
        }
        selectedItem={PACKAGING_MATERIAL_OPTIONS.find(
          (o) => o.value === value.packagingMaterials,
        )}
      />

      {value.packagingMaterials === FedexPackagingMaterial.OTHER && (
        <RedoTextInput
          label="Describe the packaging materials"
          maxLength={66}
          setValue={(s) => update({ packagingMaterialsOther: s })}
          value={value.packagingMaterialsOther ?? ""}
        />
      )}
    </Flex>
  );
});
